import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { Heart, Search, Bell, User, BookHeart, MessageCircle, Crown, LogOut, HeartHandshake } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { authApi } from '../api/auth'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import { notificationsApi } from '../api/notifications'

const navItems = [
  { to: '/dashboard', icon: Heart, label: 'Home' },
  { to: '/matches', icon: Search, label: 'Browse' },
  { to: '/interests', icon: HeartHandshake, label: 'Interests' },
  { to: '/notifications', icon: Bell, label: 'Alerts' },
  { to: '/shortlist', icon: BookHeart, label: 'Shortlist' },
  { to: '/chat', icon: MessageCircle, label: 'Chat' },
  { to: '/subscription', icon: Crown, label: 'Premium' },
  { to: '/profile', icon: User, label: 'Profile' },
]

// Mobile bottom nav: the 5 most essential
const mobileNavItems = [navItems[0], navItems[1], navItems[2], navItems[3], navItems[5], navItems[7]]

export default function Layout() {
  const navigate = useNavigate()
  const { clearAuth, accessToken } = useAuthStore()

  const { data: unreadCount } = useQuery({
    queryKey: ['notif-count'],
    queryFn: () => notificationsApi.getUnreadCount().then((r) => r.data.data?.count ?? 0),
    refetchInterval: 30_000,
  })

  const handleLogout = async () => {
    if (!window.confirm("Log out? You'll need your mobile number and OTP to sign back in.")) return
    try {
      if (accessToken) await authApi.logout(accessToken)
    } catch { /* ignore */ } finally {
      clearAuth()
      navigate('/login')
      toast.success('Logged out')
    }
  }

  return (
    <div className="min-h-screen bg-ivory-50 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 flex-col bg-ivory-25 border-r border-gold-500/25 fixed h-full z-20">
        {/* Logo */}
        <div className="px-6 pt-7 pb-6 border-b border-ivory-200">
          <p className="font-display font-semibold text-ink-900 text-xl leading-tight">Ahirwal Matrimony</p>
          <p className="kicker mt-1.5">शुभ विवाह</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] transition-all duration-150 border-l-2',
                  isActive
                    ? 'text-maroon-700 border-gold-500'
                    : 'text-ink-400 border-transparent hover:text-ink-900'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex-shrink-0">
                    <Icon
                      className={clsx(isActive ? 'text-maroon-700' : 'text-ink-400')}
                      size={17}
                      strokeWidth={1.75}
                    />
                    {to === '/notifications' && unreadCount != null && unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-maroon-700 text-champagne text-[9px] font-bold flex items-center justify-center leading-none ring-1 ring-gold-500/50">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 py-5 border-t border-ivory-200">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-400 hover:text-danger transition-all duration-150"
          >
            <LogOut size={17} strokeWidth={1.75} />
            Log out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-64 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex bg-ivory-25/95 backdrop-blur-md border-t border-ivory-200"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {mobileNavItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              clsx(
                'flex-1 flex flex-col items-center gap-1.5 pt-3 pb-2 text-[9px] font-semibold uppercase tracking-[0.14em] transition-colors relative',
                isActive ? 'text-maroon-700' : 'text-ink-400'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <Icon size={20} strokeWidth={1.75} className={isActive ? 'text-maroon-700' : 'text-ink-400'} />
                  {to === '/notifications' && unreadCount != null && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 w-4 h-4 rounded-full bg-maroon-700 text-champagne text-[9px] font-bold flex items-center justify-center leading-none ring-1 ring-gold-500/50">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
